/**
 * @nurseEdu
 * 널스에듀에서 사용하는 Header를 가져왔습니다.
 * svg는 컴포넌트로 분리하지 않고 바로 사용하였습니다.
 *
 * header의 gnb를 api로 받아오고,
 * 모바일에서 사이드 메뉴를 이용하고,
 * 검색창을 통해 검색을 하는 기능이 포함되어 있습니다.
 */

import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import HeaderUserNav from './components/HeaderUserNav';
import SideMenu from './components/SideMenu';
import MobileSearchInput from './components/HeaderMobileSearchInput';
import PageDivider from './components/Divider';
import { Flex } from './components/Flex';
import useCartCount from '../../hook/useCartCount';
import { CategoryDto, useHeaderCategories } from '../../hook/useHeaderCategories';
import useIsMobileSize from '../../hook/useIsMobileSize';
import useCurrentUser from '../../hook/useCurrentUser';
import {
  NURSEEDU_BASE_URL,
  NURSEEDU_CBT_BASE_URL,
  NURSEEDU_COMMUNITY_BASE_URL,
  NURSEEDU_STUDY_BASE_URL,
  NURSE_LINK_BASE_URL,
} from '../../constant/nurseEduConfig';
import IconUser from './components/icons/IconUser';
import ListPopup from './components/ListPopup';

import IconChevronUp from './components/icons/iconChevronUp';
import IconChevronDown from './components/icons/iconChevronDown';
import HeaderNavModal from './components/HeaderNavModal';
import useBodyScrollLock from '../../hook/useBodyScrollLock';
import { HeaderNavLinks } from './components/HeaderNavLinks';

// 헤더에 노출하는 메뉴명. 백엔드 명칭 변경 후 GNB 카테고리 라벨과 동일하다.
export const STUDY_LABEL = '스마트학습하기';
export const STUDY_BOOK_LABEL = '스마트학습구매';

// 백엔드 GNB에서 위 메뉴를 식별하기 위한 라벨 후보군.
// 백엔드 명칭 변경 배포 전/후를 모두 대응하기 위해 구 라벨을 함께 둔다.
// (배포 안정화 후 구 라벨 'e-전공학습'/'학습하기 교재'는 제거 가능)
const STUDY_MATCH_LABELS = [STUDY_LABEL, 'e-전공학습'];
const STUDY_BOOK_MATCH_LABELS = [STUDY_BOOK_LABEL, '학습하기 교재'];

// GNB 일반 메뉴 렌더 시 별도 블록으로 처리할(=제외할) 카테고리인지 판별
export const isStudyRelatedCategory = (label: string): boolean =>
  STUDY_MATCH_LABELS.includes(label) || STUDY_BOOK_MATCH_LABELS.includes(label);

// 스마트학습구매의 하위 메뉴. 노출 라벨과 순서 모두 백엔드 GNB children을 그대로 사용.
export const getStudyBookMenuItems = (
  categories: CategoryDto[] | undefined
): { label: string; categoryId: number }[] => {
  const studyBookCategory = categories?.find((c) => STUDY_BOOK_MATCH_LABELS.includes(c.label));
  return (studyBookCategory?.children ?? []).map(({ label, categoryId }) => ({
    label,
    categoryId,
  }));
};

const CBT_LIST = [
  { label: '시험 CBT', path: '/' },
  { label: '나의 구매 시험', path: '/mycbt/purchase' },
  { label: '나의 관심 시험', path: '/mycbt/archive' },
  { label: 'CBT 결과 보기', path: '/mycbt/review' },
  { label: '오답노트', path: '/mycbt/note' },
];

// 스마트학습하기는 외부 스터디 모듈로 나가는 링크 메뉴 (CBT 메뉴와 동일 패턴)
const STUDY_LIST = [
  { label: '나의학습', path: '/study', base: NURSEEDU_STUDY_BASE_URL },
  { label: '나의시험', path: '/exam', base: NURSEEDU_STUDY_BASE_URL },
  { label: '마이노트', path: '/mynote/problems', base: NURSEEDU_STUDY_BASE_URL },
  { label: '진도율', path: '/profile', base: NURSEEDU_STUDY_BASE_URL },
  { label: '대간협 모의고사', path: '/exam/assessment', base: NURSEEDU_STUDY_BASE_URL },
];

const MYPAGE_LIST = [
  { label: '마이페이지', path: '/mypage', base: NURSEEDU_BASE_URL },
  { label: '나의 시험 CBT', path: '/mycbt/archive' },
];

const HeaderNurseEdu = () => {
  const router = useRouter();
  const { user } = useCurrentUser();

  const [keywords, setKeywords] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchInputOpen, setIsSearchInputOpen] = useState(false);

  const { categories, isLoading } = useHeaderCategories();
  const { cartCount } = useCartCount({ enabled: !!user });

  const isMobileSize = useIsMobileSize();

  useEffect(() => {
    const cleanup = onLoadFont();
    return cleanup;
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [router]);

  // 스마트학습구매 하위 메뉴 (백엔드 GNB children 기반)
  const studyBookMenuItems = getStudyBookMenuItems(categories);

  // 스마트학습구매 hover 드롭다운에 넘길 ListPopup 형식
  const studyBookPopupList = studyBookMenuItems.map(({ label, categoryId }) => ({
    label,
    path: `/list/${categoryId}`,
    base: NURSEEDU_BASE_URL,
  }));

  const handleSearch = async () => {
    if (window.innerWidth < 768) {
      setIsSearchInputOpen(true);
      return;
    }

    if (keywords.trim() === '') {
      alert('1글자 이상 검색해주세요.');
      return;
    }

    const searchUrl = `${NURSEEDU_BASE_URL}/search?keywords=${keywords}`;
    router.push(searchUrl, undefined, { shallow: true });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeywords(e.target.value);
  };

  const [isHeaderModalOpen, setIsHeaderModalOpen] = useState(false);
  useBodyScrollLock(isHeaderModalOpen);

  const handleChevronBtnClick = () => setIsHeaderModalOpen((prev) => !prev);
  const onHeaderNavModalClose = () => setIsHeaderModalOpen(false);

  return (
    <StyledContainer>
      {isHeaderModalOpen && <StyledDim onClick={onHeaderNavModalClose} />}
      <HeaderUserNav />
      <header>
        <StyledHeader>
          <div>
            <div className="bl-left">
              <Link href={NURSEEDU_BASE_URL} className="logo-wrapper">
                <StyledLogo src="/images/logo/nurseEdu/logo.svg" />
              </Link>
              <div className="bl-ic">
                <div className="search">
                  <svg
                    width={20}
                    height={20}
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="ic-magnifier"
                    onClick={handleSearch}
                  >
                    <path
                      d="M5.5 0C8.53757 0 11 2.46243 11 5.5C11 6.83879 10.5217 8.06586 9.72656 9.01962L13.8536 13.1464C14.0488 13.3417 14.0488 13.6583 13.8536 13.8536C13.68 14.0271 13.4106 14.0464 13.2157 13.9114L13.1464 13.8536L9.01962 9.72656C8.06586 10.5217 6.83879 11 5.5 11C2.46243 11 0 8.53757 0 5.5C0 2.46243 2.46243 0 5.5 0ZM5.5 1C3.01472 1 1 3.01472 1 5.5C1 7.98528 3.01472 10 5.5 10C7.98528 10 10 7.98528 10 5.5C10 3.01472 7.98528 1 5.5 1Z"
                      fill={'#222222'}
                    />
                  </svg>
                  <input
                    type="text"
                    placeholder="검색어를 입력해 주세요."
                    value={keywords}
                    onChange={handleInputChange}
                    onKeyDown={(e) => {
                      if (e.key.toLowerCase() === 'enter') {
                        handleSearch();
                      }
                    }}
                  />
                </div>
                <StyledCartIconWrapper>
                  <Link href={`${NURSEEDU_BASE_URL}/orders/cart`} prefetch={false}>
                    <Flex $justify="center" $align="center">
                      <svg
                        width={20}
                        height={20}
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M1.00442 0.039071C0.812334 -0.0248682 0.602714 -0.00988235 0.421676 0.0807322C0.240637 0.171347 0.10301 0.330167 0.039071 0.522255C-0.0248683 0.714343 -0.00988227 0.923963 0.0807323 1.105C0.171347 1.28604 0.330167 1.42367 0.522255 1.48761L0.791821 1.57712C1.47947 1.806 1.93519 1.9596 2.26985 2.11524C2.58723 2.26273 2.72456 2.38175 2.81204 2.50382C2.90054 2.62589 2.96971 2.79271 3.00938 3.1406C3.05109 3.50782 3.0521 3.98796 3.0521 4.71324V7.43128C3.0521 8.82183 3.0521 9.94383 3.17112 10.8258C3.29319 11.7413 3.55767 12.5123 4.17004 13.1247C4.78139 13.7371 5.55347 13.9995 6.46898 14.1236C7.3499 14.2416 8.4719 14.2416 9.86246 14.2416H18.0562C18.2586 14.2416 18.4526 14.1613 18.5957 14.0182C18.7388 13.8751 18.8192 13.681 18.8192 13.4787C18.8192 13.2764 18.7388 13.0823 18.5957 12.9392C18.4526 12.7962 18.2586 12.7158 18.0562 12.7158H9.9184C8.45868 12.7158 7.44043 12.7137 6.67141 12.611C5.92578 12.5103 5.53008 12.3262 5.2483 12.0454C5.14574 11.9428 5.05839 11.826 4.98891 11.6986H15.0646C15.5203 11.6986 15.9211 11.6986 16.2547 11.663C16.6148 11.6233 16.9648 11.5368 17.2923 11.3201C17.6219 11.1035 17.8386 10.8166 18.0166 10.5003C18.1803 10.2093 18.339 9.84008 18.5181 9.42098L18.9931 8.3122C19.3847 7.40076 19.7113 6.63682 19.875 6.01631C20.0459 5.36834 20.0907 4.69188 19.6879 4.08052C19.2851 3.47019 18.6452 3.24436 17.983 3.14569C17.3472 3.0521 16.5172 3.0521 15.5244 3.0521H4.53421C4.53138 3.02394 4.52833 2.99579 4.52505 2.96767C4.4691 2.47432 4.34602 2.02165 4.05001 1.61171C3.75399 1.20075 3.36338 0.940336 2.91376 0.731804C2.48958 0.534462 1.95146 0.355429 1.31468 0.141811L1.00442 0.039071ZM4.57795 4.57795V7.37533C4.57795 8.56752 4.57897 9.46472 4.63593 10.1727H15.0269C15.5315 10.1727 15.8499 10.1717 16.091 10.1452C16.3137 10.1218 16.3992 10.0822 16.4531 10.0466C16.507 10.011 16.5772 9.9479 16.687 9.75259C16.8061 9.54101 16.9312 9.24907 17.1306 8.78623L17.5669 7.769C17.9881 6.78432 18.2688 6.12515 18.4001 5.62671C18.5282 5.14251 18.4631 4.99603 18.4143 4.92075C18.3645 4.8465 18.2546 4.7285 17.7592 4.65628C17.2496 4.57998 16.5334 4.57795 15.4633 4.57795H4.57795ZM4.06933 17.5476C4.06933 18.1546 4.31047 18.7368 4.7397 19.166C5.16892 19.5953 5.75108 19.8364 6.3581 19.8364C6.96512 19.8364 7.54728 19.5953 7.9765 19.166C8.40573 18.7368 8.64687 18.1546 8.64687 17.5476C8.64687 16.9406 8.40573 16.3584 7.9765 15.9292C7.54728 15.5 6.96512 15.2589 6.3581 15.2589C5.75108 15.2589 5.16892 15.5 4.7397 15.9292C4.31047 16.3584 4.06933 16.9406 4.06933 17.5476ZM6.3581 18.3105C6.15576 18.3105 5.96171 18.2302 5.81863 18.0871C5.67556 17.944 5.59518 17.75 5.59518 17.5476C5.59518 17.3453 5.67556 17.1512 5.81863 17.0082C5.96171 16.8651 6.15576 16.7847 6.3581 16.7847C6.56044 16.7847 6.75449 16.8651 6.89757 17.0082C7.04064 17.1512 7.12102 17.3453 7.12102 17.5476C7.12102 17.75 7.04064 17.944 6.89757 18.0871C6.75449 18.2302 6.56044 18.3105 6.3581 18.3105ZM15.5132 19.8364C14.9061 19.8364 14.324 19.5953 13.8948 19.166C13.4655 18.7368 13.2244 18.1546 13.2244 17.5476C13.2244 16.9406 13.4655 16.3584 13.8948 15.9292C14.324 15.5 14.9061 15.2589 15.5132 15.2589C16.1202 15.2589 16.7023 15.5 17.1316 15.9292C17.5608 16.3584 17.8019 16.9406 17.8019 17.5476C17.8019 18.1546 17.5608 18.7368 17.1316 19.166C16.7023 19.5953 16.1202 19.8364 15.5132 19.8364ZM14.7502 17.5476C14.7502 17.75 14.8306 17.944 14.9737 18.0871C15.1168 18.2302 15.3108 18.3105 15.5132 18.3105C15.7155 18.3105 15.9096 18.2302 16.0526 18.0871C16.1957 17.944 16.2761 17.75 16.2761 17.5476C16.2761 17.3453 16.1957 17.1512 16.0526 17.0082C15.9096 16.8651 15.7155 16.7847 15.5132 16.7847C15.3108 16.7847 15.1168 16.8651 14.9737 17.0082C14.8306 17.1512 14.7502 17.3453 14.7502 17.5476Z"
                          fill={'#222222'}
                        />
                      </svg>
                    </Flex>
                    {!!cartCount?.count && (
                      <Flex as="span" $justify="center" $align="center" className="cart-count">
                        {cartCount.count}
                      </Flex>
                    )}
                  </Link>
                </StyledCartIconWrapper>
                <StyledIconWrapper onClick={() => setIsMenuOpen(true)}>
                  <svg
                    width={20}
                    height={20}
                    viewBox="0 0 20 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="ic-hamburger"
                  >
                    <path
                      d="M0.750859 13.7487H19.2509C19.6651 13.7487 20.0009 14.0845 20.0009 14.4987C20.0009 14.8784 19.7187 15.1922 19.3526 15.2419L19.2509 15.2487H0.750859C0.336646 15.2487 0.000859261 14.9129 0.000859261 14.4987C0.000859261 14.119 0.283013 13.8052 0.649089 13.7556L0.750859 13.7487H19.2509H0.750859ZM0.750859 7.25172H19.2509C19.6651 7.25172 20.0009 7.58751 20.0009 8.00172C20.0009 8.38142 19.7187 8.69521 19.3526 8.74487L19.2509 8.75172H0.750859C0.336646 8.75172 0.000859261 8.41593 0.000859261 8.00172C0.000859261 7.62202 0.283013 7.30823 0.649089 7.25857L0.750859 7.25172H19.2509H0.750859ZM0.75 0.751953H19.25C19.6642 0.751953 20 1.08774 20 1.50195C20 1.88165 19.7178 2.19544 19.3518 2.24511L19.25 2.25195H0.75C0.335786 2.25195 0 1.91617 0 1.50195C0 1.12226 0.282154 0.808462 0.64823 0.7588L0.75 0.751953H19.25H0.75Z"
                      fill={'#222'}
                    />
                  </svg>
                </StyledIconWrapper>
              </div>
            </div>
            <Flex $gap={8} className="bl-right">
              <HeaderNavLinks active="nurseEdu" />
              <Link href={`${NURSEEDU_BASE_URL}/orders/cart`} className="bl-cart" prefetch={false}>
                <Flex $justify="center" $align="center">
                  <svg
                    width={20}
                    height={20}
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M1.00442 0.039071C0.812334 -0.0248682 0.602714 -0.00988235 0.421676 0.0807322C0.240637 0.171347 0.10301 0.330167 0.039071 0.522255C-0.0248683 0.714343 -0.00988227 0.923963 0.0807323 1.105C0.171347 1.28604 0.330167 1.42367 0.522255 1.48761L0.791821 1.57712C1.47947 1.806 1.93519 1.9596 2.26985 2.11524C2.58723 2.26273 2.72456 2.38175 2.81204 2.50382C2.90054 2.62589 2.96971 2.79271 3.00938 3.1406C3.05109 3.50782 3.0521 3.98796 3.0521 4.71324V7.43128C3.0521 8.82183 3.0521 9.94383 3.17112 10.8258C3.29319 11.7413 3.55767 12.5123 4.17004 13.1247C4.78139 13.7371 5.55347 13.9995 6.46898 14.1236C7.3499 14.2416 8.4719 14.2416 9.86246 14.2416H18.0562C18.2586 14.2416 18.4526 14.1613 18.5957 14.0182C18.7388 13.8751 18.8192 13.681 18.8192 13.4787C18.8192 13.2764 18.7388 13.0823 18.5957 12.9392C18.4526 12.7962 18.2586 12.7158 18.0562 12.7158H9.9184C8.45868 12.7158 7.44043 12.7137 6.67141 12.611C5.92578 12.5103 5.53008 12.3262 5.2483 12.0454C5.14574 11.9428 5.05839 11.826 4.98891 11.6986H15.0646C15.5203 11.6986 15.9211 11.6986 16.2547 11.663C16.6148 11.6233 16.9648 11.5368 17.2923 11.3201C17.6219 11.1035 17.8386 10.8166 18.0166 10.5003C18.1803 10.2093 18.339 9.84008 18.5181 9.42098L18.9931 8.3122C19.3847 7.40076 19.7113 6.63682 19.875 6.01631C20.0459 5.36834 20.0907 4.69188 19.6879 4.08052C19.2851 3.47019 18.6452 3.24436 17.983 3.14569C17.3472 3.0521 16.5172 3.0521 15.5244 3.0521H4.53421C4.53138 3.02394 4.52833 2.99579 4.52505 2.96767C4.4691 2.47432 4.34602 2.02165 4.05001 1.61171C3.75399 1.20075 3.36338 0.940336 2.91376 0.731804C2.48958 0.534462 1.95146 0.355429 1.31468 0.141811L1.00442 0.039071ZM4.57795 4.57795V7.37533C4.57795 8.56752 4.57897 9.46472 4.63593 10.1727H15.0269C15.5315 10.1727 15.8499 10.1717 16.091 10.1452C16.3137 10.1218 16.3992 10.0822 16.4531 10.0466C16.507 10.011 16.5772 9.9479 16.687 9.75259C16.8061 9.54101 16.9312 9.24907 17.1306 8.78623L17.5669 7.769C17.9881 6.78432 18.2688 6.12515 18.4001 5.62671C18.5282 5.14251 18.4631 4.99603 18.4143 4.92075C18.3645 4.8465 18.2546 4.7285 17.7592 4.65628C17.2496 4.57998 16.5334 4.57795 15.4633 4.57795H4.57795ZM4.06933 17.5476C4.06933 18.1546 4.31047 18.7368 4.7397 19.166C5.16892 19.5953 5.75108 19.8364 6.3581 19.8364C6.96512 19.8364 7.54728 19.5953 7.9765 19.166C8.40573 18.7368 8.64687 18.1546 8.64687 17.5476C8.64687 16.9406 8.40573 16.3584 7.9765 15.9292C7.54728 15.5 6.96512 15.2589 6.3581 15.2589C5.75108 15.2589 5.16892 15.5 4.7397 15.9292C4.31047 16.3584 4.06933 16.9406 4.06933 17.5476ZM6.3581 18.3105C6.15576 18.3105 5.96171 18.2302 5.81863 18.0871C5.67556 17.944 5.59518 17.75 5.59518 17.5476C5.59518 17.3453 5.67556 17.1512 5.81863 17.0082C5.96171 16.8651 6.15576 16.7847 6.3581 16.7847C6.56044 16.7847 6.75449 16.8651 6.89757 17.0082C7.04064 17.1512 7.12102 17.3453 7.12102 17.5476C7.12102 17.75 7.04064 17.944 6.89757 18.0871C6.75449 18.2302 6.56044 18.3105 6.3581 18.3105ZM15.5132 19.8364C14.9061 19.8364 14.324 19.5953 13.8948 19.166C13.4655 18.7368 13.2244 18.1546 13.2244 17.5476C13.2244 16.9406 13.4655 16.3584 13.8948 15.9292C14.324 15.5 14.9061 15.2589 15.5132 15.2589C16.1202 15.2589 16.7023 15.5 17.1316 15.9292C17.5608 16.3584 17.8019 16.9406 17.8019 17.5476C17.8019 18.1546 17.5608 18.7368 17.1316 19.166C16.7023 19.5953 16.1202 19.8364 15.5132 19.8364ZM14.7502 17.5476C14.7502 17.75 14.8306 17.944 14.9737 18.0871C15.1168 18.2302 15.3108 18.3105 15.5132 18.3105C15.7155 18.3105 15.9096 18.2302 16.0526 18.0871C16.1957 17.944 16.2761 17.75 16.2761 17.5476C16.2761 17.3453 16.1957 17.1512 16.0526 17.0082C15.9096 16.8651 15.7155 16.7847 15.5132 16.7847C15.3108 16.7847 15.1168 16.8651 14.9737 17.0082C14.8306 17.1512 14.7502 17.3453 14.7502 17.5476Z"
                      fill={'#222222'}
                    />
                  </svg>
                </Flex>
                {!!cartCount?.count && (
                  <Flex as="span" $justify="center" $align="center" className="cart-count">
                    {cartCount.count}
                  </Flex>
                )}
              </Link>
              <div className="bl-user">
                <Flex $justify="center" $align="center">
                  <IconUser fill="#222222" />
                </Flex>
                <ListPopup list={MYPAGE_LIST} />
              </div>
            </Flex>
          </div>
          {!isMobileSize && <PageDivider />}
          <StyledNavContainer>
            {categories?.map(({ label, categoryId }) => {
              if (isStudyRelatedCategory(label)) return null; // 학습 메뉴는 아래 별도 블록에서 렌더

              const href = `${NURSEEDU_BASE_URL}/list/${categoryId}`;
              return (
                <Link key={categoryId} href={href}>
                  {label}
                </Link>
              );
            })}

            {/* 스마트학습구매 — GNB children 기반 hover 드롭다운 */}
            {studyBookMenuItems.length > 0 && (
              <StyledListPoppuLink>
                <Link href={`${NURSEEDU_BASE_URL}/list/${studyBookMenuItems[0].categoryId}`}>
                  {STUDY_BOOK_LABEL}
                </Link>
                <ListPopup list={studyBookPopupList} />
              </StyledListPoppuLink>
            )}

            {/* 스마트학습하기 — 외부 스터디 모듈 링크 메뉴 (CBT 메뉴와 동일 패턴) */}
            <StyledListPoppuLink>
              <Link className={isLoading ? 'hidden' : ''} href={NURSEEDU_STUDY_BASE_URL}>
                {STUDY_LABEL}
              </Link>
              <ListPopup list={STUDY_LIST} />
            </StyledListPoppuLink>

            <StyledListPoppuLink>
              <Link className={isLoading ? 'hidden' : 'active'} href={NURSEEDU_CBT_BASE_URL}>
                CBT
              </Link>
              <ListPopup list={CBT_LIST} />
            </StyledListPoppuLink>
            <Link
              className={isLoading ? 'hidden' : ''}
              href={NURSEEDU_COMMUNITY_BASE_URL}
              prefetch={false}
            >
              커뮤니티
            </Link>
            <Link className={isLoading ? 'hidden' : ''} href={NURSE_LINK_BASE_URL}>
              채용공고
            </Link>

            <StyledGradientBox className={isLoading ? 'hidden' : ''}>
              <StyleFixedChevronButton
                onClick={handleChevronBtnClick}
                aria-label={isHeaderModalOpen ? '전체 메뉴 닫기' : '전체 메뉴 열기'}
              >
                {isHeaderModalOpen ? (
                  <IconChevronUp width={20} height={20} />
                ) : (
                  <IconChevronDown width={20} height={20} />
                )}
              </StyleFixedChevronButton>
            </StyledGradientBox>
          </StyledNavContainer>
        </StyledHeader>
        <PageDivider />
        <HeaderNavModal
          isOpen={isHeaderModalOpen}
          categories={categories}
          onClose={onHeaderNavModalClose}
        />
      </header>

      <SideMenu isOpen={isMenuOpen} closeMenu={() => setIsMenuOpen(false)} />
      {isSearchInputOpen && (
        <MobileSearchInput
          keywords={keywords}
          handleSearchInputOpen={() => setIsSearchInputOpen((prev) => !prev)}
          handleInputChange={handleInputChange}
        />
      )}
    </StyledContainer>
  );
};

export default HeaderNurseEdu;

const onLoadFont = () => {
  const link = document.createElement('link');
  link.href =
    'https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/static/pretendard-dynamic-subset.min.css';
  link.rel = 'stylesheet';
  link.id = 'pretendard-font';
  document.head.appendChild(link);

  return () => {
    const fontLink = document.getElementById('pretendard-font');
    if (fontLink) {
      document.head.removeChild(fontLink);
    }
  };
};

const StyledContainer = styled.div`
  * {
    font-family: 'Pretendard Variable', Pretendard, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  header {
    min-width: 1200px;
  }

  .divider-container > div {
    min-width: 1220px;
  }

  .hidden {
    display: none;
  }

  @media (min-width: 769px) {
    header {
      padding-left: 20px;
    }
  }

  @media (max-width: 768px) {
    header {
      min-width: unset;
    }

    .divider-container > div {
      min-width: unset;
    }
  }
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1220px;
  margin: 0 auto;
  padding-top: 47px;

  .logo-wrapper {
    height: 48px;
    cursor: pointer;

    @media (max-width: 768px) {
      height: 38px;
    }
  }

  .cart {
    border-radius: 50%;
    background-color: #f5f5f5;
  }

  > div {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 9px 20px;

    .bl-left {
      display: flex;
      gap: 16px;
      justify-content: space-between;
      align-items: center;

      .bl-ic {
        display: flex;
        gap: 16px;
        align-items: center;
      }
    }

    @media (min-width: 769px) {
      padding: 31px 0px;
    }
  }

  .search {
    position: relative;
    display: flex;
    align-items: center;

    input {
      width: 400px;
      height: 36px;
      padding: 0 40px;
      box-sizing: border-box;
      font-weight: 400;
      font-size: 15px;
      line-height: 130%;
      letter-spacing: -0.01em;
      border: 1px solid #d9d9d9;
      border-radius: 18px;

      &:focus {
        outline: none;
      }

      &::placeholder {
        color: '#767676';
      }
    }

    @media (min-width: 769px) {
      svg {
        position: absolute;
        left: 16px;
        width: 14px;
        height: 14px;
      }
    }

    @media (max-width: 768px) {
      svg {
        width: 20px;
        height: 20px;
      }

      input {
        display: none;
      }
    }
  }

  .ic-hamburger {
    width: 20px;
    height: 20px;

    @media (min-width: 769px) {
      display: none;
    }
  }

  .bl-cart {
    position: relative;
    display: inline-block;

    > div {
      width: 40px;
      height: 40px;

      border-radius: 50%;
      cursor: pointer;

      &:hover {
        background-color: #f5f5f5;
      }
    }

    > span {
      position: absolute;
      top: -4px;
      right: -4px;
      width: 18px;
      height: 18px;
      color: #fff;
      font-weight: 700;
      font-size: 13px;
      font-style: inherit;
      line-height: 130%;
      letter-spacing: -0.01em;
      border-radius: 50%;
      background-color: #2f3982;
    }
  }

  .bl-user {
    position: relative;
    display: inline-block;
    border-radius: 50%;
    cursor: pointer;

    > div {
      width: 40px;
      height: 40px;
    }

    &:hover {
      background-color: #f5f5f5;

      path {
        stroke: #283176;
      }

      .list-popup {
        display: block;
        top: 30px;
        left: -100px;
      }
    }
  }

  @media (max-width: 768px) {
    padding-top: 0;
  }

  @media (min-width: 769px) {
    .ic-search {
      position: absolute;
      left: 16px;
    }
  }

  @media (max-width: 768px) {
    > div {
      .bl-left {
        flex-grow: 1;
      }
    }

    .bl-search {
      input {
        display: none;
      }
    }

    .bl-cart,
    .bl-user {
      display: none;
    }

    nav {
      a {
        padding: 0;
        font-size: 16px;
        font-weight: 700;
        line-height: 140%;
        letter-spacing: -0.32px;

        display: flex;
        align-items: center;
        justify-content: center;

        &.active {
          border-bottom: 2px solid #38459f;

          @media (max-width: 767px) {
            border-bottom: 0;
          }
        }
      }
    }
  }
`;

const StyledIconWrapper = styled.div`
  height: 20px;
`;

const StyledCartIconWrapper = styled.div`
  position: relative;

  div {
    width: 20px;
    height: 20px;
  }

  span {
    position: absolute;
    top: -10px;
    right: -10px;
    width: 14px;
    height: 14px;
    color: #fff;
    font-weight: 700;
    font-size: 13px;
    line-height: 130%;
    letter-spacing: -0.01em;
    border-radius: 50%;
    background-color: #2f3982;
  }

  @media (min-width: 769px) {
    display: none;
  }
`;

const StyledLogo = styled.img`
  width: 100px;
  height: 48px;

  @media (max-width: 768px) {
    height: 38px;
  }
`;

const StyledListPoppuLink = styled(Flex)`
  @media (min-width: 769px) {
    &:hover {
      .list-popup {
        display: block;
      }
    }
  }
`;

const StyledNavContainer = styled.nav`
  display: flex;
  padding-left: 8px;
  overflow-x: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 767px) {
    gap: 18px;
    height: 50px;
    padding-right: 50px; /* 메뉴 모달을 여는 버튼 넓이만큼 오른쪽 패딩 추가 */
    padding-left: 20px;
  }

  a {
    padding: 18px 20px;
    color: rgb(34, 34, 34);
    font-weight: 600;
    font-size: 17px;
    line-height: 140%;
    letter-spacing: -0.025em;
    white-space: nowrap;

    &.active {
      color: #384582;
      font-weight: 600;
    }
  }
`;

const StyledGradientBox = styled.div`
  position: absolute;
  right: 0;
  top: 61px; /* 바텀 헤더의 높이에서 6px 아래로 위치해야하고, 사이트 헤더가 높아진 만큼 높이 추가 2px */
  flex-shrink: 0;
  flex-grow: 0;

  width: 50px;
  height: 40px;
  padding-right: 5px;

  display: flex;
  flex-direction: row-reverse;
  align-items: center;

  background: linear-gradient(to right, rgba(255, 255, 255, 0), rgb(255, 255, 255) 20%);
  z-index: 12;

  @media (min-width: 768px) {
    display: none;
  }

  .hidden {
    display: none;
  }
`;

const StyleFixedChevronButton = styled.button`
  flex-shrink: 0;
  width: 28px;
  height: 28px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 4px;
  border: 1px solid #d9dce1;
  background-color: white;

  z-index: 12;
  cursor: pointer;

  @media (min-width: 768px) {
    display: none;
  }
`;

const StyledDim = styled.div`
  position: fixed;
  top: 107px; /* 현재 메뉴 모달과 동일하게 top으로 위치를 조정하고 있기 때문에 메뉴 모달 위치 수정 시 Dim도 맞춰서 바꿔줘야 함 */
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background-color: rgba(0, 0, 0, 0.6);

  @media (min-width: 768px) {
    display: none;
  }
`;
